import { useQuery } from '@tanstack/react-query';
import {
  ApiAuthError,
  fetchAdoptionSeries,
  fetchHarnessDistribution,
  fetchModelDistribution,
  fetchOverview,
  fetchTokenSeries,
  fetchTopSkills,
  fetchTopSprinkles,
  fetchTopUsers,
  type DashboardWindow,
} from '../api/client';
import { SignInScreen } from '../auth/SignInScreen';
import { useToken } from '../auth/TokenContext';
import { AdoptionLineChart } from '../components/charts/AdoptionLineChart';
import { HarnessDistributionDonut } from '../components/charts/HarnessDistributionDonut';
import { ModelDistributionDonut } from '../components/charts/ModelDistributionDonut';
import { TokenStackedAreaChart } from '../components/charts/TokenStackedAreaChart';
import { TokensVsCostLineChart } from '../components/charts/TokensVsCostLineChart';
import { TopSkillsBarChart } from '../components/charts/TopSkillsBarChart';
import { TopSprinklesBarChart } from '../components/charts/TopSprinklesBarChart';
import { TopUsersBarChart } from '../components/charts/TopUsersBarChart';
import { FiltersBar } from '../components/FiltersBar';
import { KpiCard } from '../components/KpiCard';
import { useFilters } from '../filters/FiltersContext';

const TOP_USERS_LIMIT = 10;
const TOP_SKILLS_LIMIT = 10;
const TOP_SPRINKLES_LIMIT = 10;
const QUERY_KEYS = [
  'overview',
  'adoption-series',
  'token-series',
  'top-users',
  'top-skills',
  'top-sprinkles',
  'model-distribution',
];

function formatPercent(value: number): string {
  return `${(value * 100).toFixed(0)}%`;
}

function formatNumber(value: number): string {
  return value.toLocaleString('en-US');
}

function bucketWord(g: 'hour' | 'day' | 'month'): string {
  if (g === 'hour') return 'hour';
  if (g === 'month') return 'month';
  return 'day';
}

export function Overview(): JSX.Element {
  const { token, signOut } = useToken();
  const { range, origin, granularity } = useFilters();

  const window: DashboardWindow = { from: range.from, to: range.to, origin };
  const tokenKey = token?.slice(-8);

  const overviewQuery = useQuery({
    queryKey: ['overview', range.from, range.to, origin, tokenKey],
    queryFn: () => fetchOverview(window, token),
    enabled: !!token,
  });

  const adoptionQuery = useQuery({
    queryKey: ['adoption-series', range.from, range.to, origin, granularity, tokenKey],
    queryFn: () => fetchAdoptionSeries(window, token, granularity),
    enabled: !!token,
  });

  const tokenSeriesQuery = useQuery({
    queryKey: ['token-series', range.from, range.to, origin, granularity, tokenKey],
    queryFn: () => fetchTokenSeries(window, token, granularity),
    enabled: !!token,
  });

  const topUsersQuery = useQuery({
    queryKey: ['top-users', range.from, range.to, origin, TOP_USERS_LIMIT, tokenKey],
    queryFn: () => fetchTopUsers(window, TOP_USERS_LIMIT, token),
    enabled: !!token,
  });

  const topSkillsQuery = useQuery({
    queryKey: ['top-skills', range.from, range.to, origin, TOP_SKILLS_LIMIT, tokenKey],
    queryFn: () => fetchTopSkills(window, TOP_SKILLS_LIMIT, token),
    enabled: !!token,
  });

  const topSprinklesQuery = useQuery({
    queryKey: ['top-sprinkles', range.from, range.to, origin, TOP_SPRINKLES_LIMIT, tokenKey],
    queryFn: () => fetchTopSprinkles(window, TOP_SPRINKLES_LIMIT, token),
    enabled: !!token,
  });

  const modelDistributionQuery = useQuery({
    queryKey: ['model-distribution', range.from, range.to, origin, tokenKey],
    queryFn: () => fetchModelDistribution(window, token),
    enabled: !!token,
  });

  const harnessDistributionQuery = useQuery({
    queryKey: ['harness-distribution', range.from, range.to, origin, tokenKey],
    queryFn: () => fetchHarnessDistribution(window, token),
    enabled: !!token,
  });

  const allQueries = [
    overviewQuery,
    adoptionQuery,
    tokenSeriesQuery,
    topUsersQuery,
    topSkillsQuery,
    topSprinklesQuery,
    modelDistributionQuery,
    harnessDistributionQuery,
  ];
  const authError = allQueries
    .map((q) => q.error)
    .find((e): e is ApiAuthError => e instanceof ApiAuthError);

  if (authError) {
    return (
      <SignInScreen
        reason={`Token rejected by the worker (HTTP ${authError.status}): ${authError.message}`}
      />
    );
  }

  const overview = overviewQuery.data;
  const isOverviewLoading = overviewQuery.isLoading;

  const isRefreshing = allQueries.some((q) => q.isFetching);

  return (
    <section className="overview">
      <FiltersBar
        isRefreshing={isRefreshing}
        invalidateKeys={QUERY_KEYS}
        showGranularity
      />

      {allQueries.some((q) => q.isError) && !authError && (
        <div className="overview-error">
          Failed to load some data.{' '}
          <button type="button" onClick={signOut}>Sign out</button>
        </div>
      )}

      <div className="kpi-strip">
        <KpiCard
          label="Active users"
          value={overview ? formatNumber(overview.current.activeUsers) : ''}
          delta={overview?.deltas.activeUsers ?? null}
          loading={isOverviewLoading}
          hint="Distinct user_id over the window"
        />
        <KpiCard
          label="Sessions"
          value={overview ? formatNumber(overview.current.sessions) : ''}
          delta={overview?.deltas.sessions ?? null}
          loading={isOverviewLoading}
          hint="Cone-aggregated (cone+scoops = 1 session)"
        />
        <KpiCard
          label="Completion rate"
          value={overview ? formatPercent(overview.current.completionRate) : ''}
          delta={overview?.deltas.completionRate ?? null}
          loading={isOverviewLoading}
          hint="v5 outcome=completed / classified sessions"
        />
        <KpiCard
          label="Completed sessions"
          value={overview ? formatNumber(overview.current.completedSessionCount) : ''}
          delta={overview?.deltas.completedSessionCount ?? null}
          loading={isOverviewLoading}
          hint="Sessions whose v5 outcome is `completed`"
        />
      </div>

      <section className="chart-section">
        <header>
          <h2>Active users per {bucketWord(granularity)}</h2>
          <p className="chart-subtitle">
            Distinct users per UTC {bucketWord(granularity)} in the selected window.
          </p>
        </header>
        <AdoptionLineChart
          points={adoptionQuery.data?.points ?? []}
          loading={adoptionQuery.isLoading}
          granularity={granularity}
        />
      </section>

      <section className="chart-section">
        <header>
          <h2>Token usage by category per {bucketWord(granularity)}</h2>
          <p className="chart-subtitle">
            Stacked tokens per UTC {bucketWord(granularity)}, split into cache read,
            cache write, output, and input. Cache reads typically dominate (~90%) —
            that's prompt caching working as intended. Hover any bucket for the
            breakdown plus an estimated USD cost (per-model pricing).
          </p>
        </header>
        <TokenStackedAreaChart
          points={tokenSeriesQuery.data?.points ?? []}
          loading={tokenSeriesQuery.isLoading}
          granularity={granularity}
        />
      </section>

      <section className="chart-section">
        <header>
          <h2>Tokens vs. cost</h2>
          <p className="chart-subtitle">
            Total tokens (left axis) and estimated USD cost (right axis) per UTC day.
            The two lines diverge when the model mix shifts — Opus runs ~5× the price
            of Sonnet, so a small Opus uptick costs more than the token trend suggests.
          </p>
        </header>
        <TokensVsCostLineChart
          points={tokenSeriesQuery.data?.points ?? []}
          loading={tokenSeriesQuery.isLoading}
        />
      </section>

      <section className="chart-section">
        <header>
          <h2>Top users by tokens</h2>
          <p className="chart-subtitle">
            Top {TOP_USERS_LIMIT} users in the window, ranked by total tokens (input +
            output + cache). user_id is the IMS sub claim — already opaque.
          </p>
        </header>
        <TopUsersBarChart
          users={topUsersQuery.data?.users ?? []}
          loading={topUsersQuery.isLoading}
        />
      </section>

      <div className="chart-row-2col">
        <section className="chart-section">
          <header>
            <h2>Model distribution</h2>
            <p className="chart-subtitle">
              Share of total tokens (input + output + cache) per LLM model in the
              window. Hover any slice for the exact count.
            </p>
          </header>
          <ModelDistributionDonut
            models={modelDistributionQuery.data?.models ?? []}
            loading={modelDistributionQuery.isLoading}
          />
        </section>

        <section className="chart-section">
          <header>
            <h2>Harness / Agent distribution</h2>
            <p className="chart-subtitle">
              Share of LLM proxy events by agent harness (SLICC, Claude Code,
              Copilot, Pi, other). Hover any slice for event count and distinct users.
            </p>
          </header>
          <HarnessDistributionDonut
            harnesses={harnessDistributionQuery.data?.harnesses ?? []}
            loading={harnessDistributionQuery.isLoading}
          />
        </section>
      </div>

      <div className="chart-row-2col">
        <section className="chart-section">
          <header>
            <h2>Top skills loaded</h2>
            <p className="chart-subtitle">
              Top {TOP_SKILLS_LIMIT} skills in the window, ranked by load count. A "skill load"
              is a <code>read_file</code> tool call against{' '}
              <code>/skills/&lt;name&gt;/SKILL.md</code> — the agent pulling a skill into its
              context. Hover for distinct sessions and users.
            </p>
          </header>
          <TopSkillsBarChart
            skills={topSkillsQuery.data?.skills ?? []}
            loading={topSkillsQuery.isLoading}
          />
        </section>

        <section className="chart-section">
          <header>
            <h2>Top sprinkles</h2>
            <p className="chart-subtitle">
              Top {TOP_SPRINKLES_LIMIT} sprinkle channels fired in the window, ranked by
              total event count (any action). A "sprinkle" is a SLICC pub/sub message —{' '}
              <code>[Sprinkle Event: &lt;channel&gt;]</code> in the LLM request body.
              Hover for distinct sessions and users.
            </p>
          </header>
          <TopSprinklesBarChart
            sprinkles={topSprinklesQuery.data?.sprinkles ?? []}
            loading={topSprinklesQuery.isLoading}
          />
        </section>
      </div>
    </section>
  );
}
